// ═══════════════════════════════════════════════════════════════════
// plazaPieces3D.jsx — THE ROOM, IN THREE DIMENSIONS (Plaza 3D, Ship 1)
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "leave the plaza exactly how it is … but flip a
// 3D switch and have the plaza be 3D with all 3D models you make —
// even V'ael, Willie, everyone, the fountain, the portals!"
//
// ⭐ EVERY PIECE HERE IS A TRANSLATION OF `plaza_1.webp`, placed by
// `plazaZones.js`. The painting decides what a thing looks like; the
// zone table decides where it stands and how big it is. Nothing in
// this file invents a position: a fixture takes the rect(s) the table
// already gives its collision box and builds itself to fill them. Move
// a box in plazaZones and the 3D fixture moves with it, in BOTH
// orientations, because portrait is the same table through scaleRect.
//
// ⚠ THIS FILE DRAWS. Plaza.js's header law — "nothing in this file
// draws a piece of the room" — still governs Plaza.js. The 3D room is
// a second RENDERER of the same table, exactly as Shatterfield3D is a
// second renderer of the same duel. It is not a second opinion about
// where anything IS: collision stays in the table, and only there.
//
// THE LAWS THIS FILE KEEPS (ie-being-models / ie-unstable-world):
//   · no boxes for anything organic; slabs only for stone and wood,
//     and those are bevelled or massed, never bare crates
//   · every texture is a cached canvas, seeded with mulberry32, and
//     NO PRNG IS EVER CALLED DURING RENDER — variety is baked in
//     useMemo and read in JSX
//   · glow is emissive. The whole room carries THREE point lights
//     (the fountain, the forge, Willy's lantern) and the sky carries
//     none. `LIGHT.GAIN` is the one knob.
//   · one useFrame per SYSTEM: the portals share one, the fountain
//     has one, the forge has one, the sky has one.
//   · population counts are DIALS at the top of the file.
// ═══════════════════════════════════════════════════════════════════
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mulberry32, canvasTex, hashId } from "./ieBeingModels";
import { RamHorn } from "./plazaFolk3D";

// ── DIALS ──────────────────────────────────────────────────────────
export const LIGHT = {
  GAIN: 1.0,        // still too dark? 1.3. lost the dread? 0.75.
  ambient: 0.85,
  hemi:    1.0,
  key:     1.6,
  fountain: 2.6,
  forge:    2.2,
  lantern:  1.4,
  exposure: 1.25,
};
export const G = (v) => v * LIGHT.GAIN;

const N_STARS  = 520;
const N_SHARDS = 14;
const N_MOTES  = 36;
const N_GEMS   = 12;   // on the fountain rim

// THE CANON COLOURS — named so nobody has to remember which hex is which force.
export const C = {
  nullus: "#B388FF", omnis: "#F2C14E", anima: "#FF4D8D", chao: "#7CE7FF",
  lyt: "#FFE9A0", lyf: "#7FE8D8", ruin: "#E8453C", shade: "#2A1540",
};

// Wall / shelf heights. The camera trails from the SOUTH, so the south
// wall is a low balustrade and the side walls are waist-high; only the
// north wall — the portal shelf — stands full height. A full box would
// put a wall between the lens and the player every time they walked
// south (the diorama bug, walkable-worlds.md).
export const WALL_N = 3.2;
export const WALL_E = 1.5;
export const WALL_S = 0.7;
export const SHELF_ROWS = 5;   // rows 0-4 are the portal shelf (plazaZones TERRAIN_SOLID)

// ── rect helpers ───────────────────────────────────────────────────
// Rects are [x0, y0, x1, y1] INCLUSIVE tiles. World x = tile x, world z
// = tile y; a tile's centre is +0.5.
export const rectBounds = (rects) => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [a, b, c, d] of rects || []) { x0 = Math.min(x0, a); y0 = Math.min(y0, b); x1 = Math.max(x1, c); y1 = Math.max(y1, d); }
  if (!isFinite(x0)) return null;
  return { x0, y0, x1, y1, cx: (x0 + x1 + 1) / 2, cz: (y0 + y1 + 1) / 2, w: x1 - x0 + 1, d: y1 - y0 + 1 };
};

// ═══════════════════════════════════════════════════════════════════
// TEXTURES — painted once, cached by key, seeded.
// ═══════════════════════════════════════════════════════════════════
export const cobbleTex = () => canvasTex("plaza3d:cobble", 512, 512, (g, w, h) => {
  const R = mulberry32(0x9a7a);
  g.fillStyle = "#1a1322"; g.fillRect(0, 0, w, h);
  // brick courses, like the painting's floor — dark slate, warm mortar
  const bh = 26, bw = 60;
  for (let y = 0; y < h; y += bh) {
    const off = ((y / bh) | 0) % 2 ? bw / 2 : 0;
    for (let x = -bw; x < w + bw; x += bw) {
      const v = R();
      const l = 34 + v * 14;
      g.fillStyle = `hsl(${262 + R() * 14}, ${18 + R() * 10}%, ${l}%)`;
      g.fillRect(x + off + 2, y + 2, bw - 4, bh - 4);
      // a highlight on the top edge so each stone has a face
      g.fillStyle = `rgba(200,180,230,${0.05 + R() * 0.05})`;
      g.fillRect(x + off + 2, y + 2, bw - 4, 2);
    }
  }
  // grime pools
  for (let i = 0; i < 40; i++) {
    const x = R() * w, y = R() * h, r = 20 + R() * 60;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `rgba(0,0,0,${0.12 + R() * 0.16})`); gr.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = gr; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
});

export const brickTex = () => canvasTex("plaza3d:brick", 512, 512, (g, w, h) => {
  const R = mulberry32(0x5b1c);
  g.fillStyle = "#120c1a"; g.fillRect(0, 0, w, h);
  const bh = 22, bw = 52;
  for (let y = 0; y < h; y += bh) {
    const off = ((y / bh) | 0) % 2 ? bw / 2 : 0;
    for (let x = -bw; x < w + bw; x += bw) {
      const l = 27 + R() * 11;
      g.fillStyle = `hsl(${268 + R() * 10}, ${16 + R() * 8}%, ${l}%)`;
      g.fillRect(x + off + 2, y + 2, bw - 4, bh - 4);
      g.fillStyle = `rgba(230,210,255,${0.04 + R() * 0.04})`;
      g.fillRect(x + off + 2, y + 2, bw - 4, 1.5);
    }
  }
  // the hairline fractures of the Unstable World, faint
  g.strokeStyle = "rgba(179,136,255,0.10)"; g.lineWidth = 1;
  for (let i = 0; i < 6; i++) {
    let x = R() * w, y = R() * h; g.beginPath(); g.moveTo(x, y);
    for (let k = 0; k < 7; k++) { x += (R() - 0.5) * 60; y += (R() - 0.2) * 50; g.lineTo(x, y); }
    g.stroke();
  }
});

export const woodTex = (key = "plaza3d:wood", hue = 28, seed = 0x77) => canvasTex(key, 256, 256, (g, w, h) => {
  const R = mulberry32(seed);
  g.fillStyle = `hsl(${hue}, 38%, 22%)`; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 90; i++) {
    g.strokeStyle = `hsla(${hue + R() * 10 - 5}, 36%, ${14 + R() * 18}%, 0.55)`;
    g.lineWidth = 1 + R() * 3;
    g.beginPath(); const y = R() * h; g.moveTo(0, y);
    for (let x = 0; x <= w; x += 32) g.lineTo(x, y + Math.sin(x * 0.05 + i) * 4 * R());
    g.stroke();
  }
});

// The tent. Patched, torn canvas — the tears glow with what is inside.
export const tentTex = () => canvasTex("plaza3d:tent", 512, 512, (g, w, h) => {
  const R = mulberry32(0x7e47);
  g.fillStyle = "#a8956f"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 700; i++) { g.fillStyle = `rgba(50,32,14,${R() * 0.16})`; g.fillRect(R() * w, R() * h, 2 + R() * 18, 1 + R() * 4); }
  // stains and grime, the big soft ones
  for (let i = 0; i < 14; i++) { const x = R() * w, y = R() * h, r = 30 + R() * 70; const gr = g.createRadialGradient(x, y, 0, x, y, r); gr.addColorStop(0, "rgba(70,48,22,0.35)"); gr.addColorStop(1, "rgba(70,48,22,0)"); g.fillStyle = gr; g.fillRect(x - r, y - r, r * 2, r * 2); }
  // patches
  const cols = ["#6f5a3c", "#5f7a78", "#5a4a3a", "#9a7b57", "#4f6d8a", "#7f6d5a", "#6a8a7a"];
  for (let i = 0; i < 22; i++) {
    const x = R() * w, y = R() * h, pw = 30 + R() * 60, ph = 24 + R() * 50;
    g.fillStyle = cols[(R() * cols.length) | 0]; g.fillRect(x, y, pw, ph);
    g.strokeStyle = "rgba(30,20,10,0.5)"; g.setLineDash([4, 5]); g.lineWidth = 2; g.strokeRect(x + 3, y + 3, pw - 6, ph - 6); g.setLineDash([]);
  }
  // tears — a dark hole with a violet glow bleeding through
  for (let i = 0; i < 9; i++) {
    const x = R() * w, y = R() * h, r = 12 + R() * 26;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, "#5b2d9a"); gr.addColorStop(0.45, "#1a0a2e"); gr.addColorStop(0.75, "rgba(20,10,30,0.9)"); gr.addColorStop(1, "rgba(20,10,30,0)");
    g.fillStyle = gr; g.beginPath();
    for (let k = 0; k < 9; k++) { const a = k / 9 * Math.PI * 2, rr = r * (0.55 + R() * 0.5); g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    g.closePath(); g.fill();
  }
});

// The vortex inside an arch. One per colour; the material's uv rotates.
export const vortexTex = (hex) => canvasTex("plaza3d:vortex:" + hex, 256, 256, (g, w, h) => {
  const cx = w / 2, cy = h / 2;
  g.fillStyle = "#07040f"; g.fillRect(0, 0, w, h);
  const col = new THREE.Color(hex);
  for (let arm = 0; arm < 3; arm++) {
    for (let i = 0; i < 260; i++) {
      const t = i / 260, a = t * Math.PI * 4.2 + arm * (Math.PI * 2 / 3), r = 6 + t * 118;
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
      const s = 2 + (1 - t) * 6;
      g.fillStyle = `rgba(${col.r * 255 | 0},${col.g * 255 | 0},${col.b * 255 | 0},${0.12 + (1 - t) * 0.5})`;
      g.beginPath(); g.arc(x, y, s, 0, Math.PI * 2); g.fill();
    }
  }
  const gr = g.createRadialGradient(cx, cy, 0, cx, cy, 40);
  gr.addColorStop(0, "#fff8"); gr.addColorStop(0.35, hex + "aa"); gr.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
});

// The sigil on each keystone — read off the painting: a skull for the
// sealed Mineriums, a trident for the Hall and the World, an ankh for
// the Quarters, an omega for the Pierre.
export const sigilTex = (id, hex) => canvasTex(`plaza3d:sigil:${id}:${hex}`, 96, 96, (g, w, h) => {
  g.clearRect(0, 0, w, h); g.strokeStyle = hex; g.fillStyle = hex; g.lineWidth = 6; g.lineCap = "round"; g.lineJoin = "round"; g.shadowColor = hex; g.shadowBlur = 10;
  const c = w / 2;
  if (id === "emperiums") { g.beginPath(); g.arc(c, 40, 22, Math.PI * 0.95, Math.PI * 2.05); g.lineTo(c + 16, 62); g.lineTo(c - 16, 62); g.closePath(); g.fill(); g.fillStyle = "#07040f"; g.beginPath(); g.arc(c - 9, 40, 6, 0, Math.PI * 2); g.arc(c + 9, 40, 6, 0, Math.PI * 2); g.fill(); g.fillRect(c - 12, 64, 24, 8); }
  else if (id === "quarters") { g.beginPath(); g.arc(c, 30, 12, 0, Math.PI * 2); g.stroke(); g.beginPath(); g.moveTo(c, 42); g.lineTo(c, 80); g.moveTo(c - 20, 56); g.lineTo(c + 20, 56); g.stroke(); }
  else if (id === "pierre") { g.beginPath(); g.arc(c, 44, 22, Math.PI * 0.75, Math.PI * 2.25); g.stroke(); g.beginPath(); g.moveTo(c - 26, 74); g.lineTo(c - 10, 74); g.moveTo(c + 10, 74); g.lineTo(c + 26, 74); g.stroke(); }
  else { g.beginPath(); g.moveTo(c, 20); g.lineTo(c, 80); g.moveTo(c - 20, 34); g.quadraticCurveTo(c - 20, 60, c, 62); g.quadraticCurveTo(c + 20, 60, c + 20, 34); g.stroke(); }
});
// Runes for the arch frames — a strip of invented glyphs in the arch's colour.
export const runeStripTex = (hex) => canvasTex("plaza3d:runes:" + hex, 64, 512, (g, w, h) => {
  const R = mulberry32(hashId(hex));
  g.fillStyle = "#2a2234"; g.fillRect(0, 0, w, h);
  g.strokeStyle = hex; g.lineWidth = 3; g.lineCap = "round"; g.shadowColor = hex; g.shadowBlur = 6;
  for (let y = 22; y < h - 10; y += 40) {
    g.beginPath();
    for (let k = 0; k < 4; k++) { const x = 14 + R() * 36, yy = y + (R() - 0.5) * 22; if (k === 0) g.moveTo(x, yy); else g.lineTo(x, yy); }
    g.stroke();
  }
});

// The fountain's water: liquid N'ullus light, filaments not waves.
export const waterTex = () => canvasTex("plaza3d:water", 256, 256, (g, w, h) => {
  const R = mulberry32(0x0a11);
  g.fillStyle = "#1e0c48"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 70; i++) {
    g.strokeStyle = `rgba(${190 + R() * 60 | 0},${150 + R() * 80 | 0},255,${0.3 + R() * 0.45})`;
    g.lineWidth = 1.5 + R() * 2.5; g.beginPath();
    let x = R() * w, y = R() * h; g.moveTo(x, y);
    for (let k = 0; k < 6; k++) { x += (R() - 0.5) * 50; y += (R() - 0.5) * 50; g.lineTo(x, y); }
    g.stroke();
  }
});

// Painted words. Cached by text + colour (ie-realtime-perf 5a).
export const signTex = (text, color = "#f1e2b8", bg = "#3b2414", w = 512, h = 128, font = "bold 54px Georgia, serif") =>
  canvasTex(`plaza3d:sign:${text}:${color}:${bg}`, w, h, (g) => {
    g.fillStyle = bg; g.fillRect(0, 0, w, h);
    g.strokeStyle = color + "88"; g.lineWidth = 4; g.strokeRect(8, 8, w - 16, h - 16);
    g.fillStyle = color; g.font = font; g.textAlign = "center"; g.textBaseline = "middle";
    g.shadowColor = "#000"; g.shadowBlur = 8;
    const lines = String(text).split("\n");
    lines.forEach((ln, i) => g.fillText(ln, w / 2, h / 2 + (i - (lines.length - 1) / 2) * (h / (lines.length + 0.4))));
  });

const paperTex = () => canvasTex("plaza3d:paper", 128, 128, (g, w, h) => {
  const R = mulberry32(0x9a9e);
  g.fillStyle = "#e6d8b4"; g.fillRect(0, 0, w, h);
  g.strokeStyle = "#5a4a3a"; g.lineWidth = 2;
  for (let y = 24; y < h - 10; y += 16) { g.beginPath(); g.moveTo(16, y); g.lineTo(16 + 40 + R() * 60, y); g.stroke(); }
  g.fillStyle = "#8a1d1d"; g.beginPath(); g.arc(w - 26, h - 24, 9, 0, Math.PI * 2); g.fill();
});

// ═══════════════════════════════════════════════════════════════════
// SHARED MATERIALS (module-level, one each)
// ═══════════════════════════════════════════════════════════════════
export const STONE = { color: "#6d6478", roughness: 0.85, metalness: 0.05 };
export const DARK_STONE = { color: "#3a3242", roughness: 0.9 };
export const IRON = { color: "#2b2530", roughness: 0.5, metalness: 0.7 };
const BRASS = { color: "#b08a3c", roughness: 0.35, metalness: 0.8 };

// ═══════════════════════════════════════════════════════════════════
// THE ROOM — floor, walls, the portal shelf.
// ═══════════════════════════════════════════════════════════════════
export function Room({ cols, rows }) {
  const floor = useMemo(() => { const t = cobbleTex(); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(cols / 4, rows / 4); return t; }, [cols, rows]);
  const shelfZ = SHELF_ROWS;               // north wall face
  // one brick canvas, cloned ONCE per wall so each carries its own repeat
  const walls = useMemo(() => {
    const base = brickTex(); base.wrapS = base.wrapT = THREE.RepeatWrapping;
    const mk = (rx, ry) => { const t = base.clone(); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(rx, ry); t.needsUpdate = true; return t; };
    return { n: mk(cols / 3, WALL_N / 1.4), shelf: mk(cols / 3, shelfZ / 3), side: mk((rows - shelfZ) / 3, WALL_E / 1.4), s: mk(cols / 3, WALL_S) };
  }, [cols, rows, shelfZ]);
  const wallMat = (t) => <meshStandardMaterial map={t} roughness={0.92} />;
  return (
    <group>
      {/* floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cols / 2, 0, rows / 2]} receiveShadow>
        <planeGeometry args={[cols, rows]} />
        <meshStandardMaterial map={floor} roughness={0.95} />
      </mesh>
      {/* NORTH — the portal shelf: a full-height wall along z=shelfZ, and a
          raised slab behind it the arches stand on */}
      <mesh position={[cols / 2, WALL_N / 2, shelfZ]} receiveShadow castShadow>
        <boxGeometry args={[cols, WALL_N, 0.5]} />
        {wallMat(walls.n)}
      </mesh>
      <mesh position={[cols / 2, WALL_N - 0.25, shelfZ / 2]} receiveShadow>
        <boxGeometry args={[cols, 0.5, shelfZ]} />
        {wallMat(walls.shelf)}
      </mesh>
      {/* a cornice line so the shelf reads as architecture */}
      <mesh position={[cols / 2, WALL_N + 0.02, shelfZ + 0.18]}>
        <boxGeometry args={[cols, 0.16, 0.9]} />
        <meshStandardMaterial {...STONE} />
      </mesh>

      {/* EAST / WEST — waist-high, capped */}
      {[0.5, cols - 0.5].map((x, i) => (
        <group key={i}>
          <mesh position={[x, WALL_E / 2, (shelfZ + rows) / 2]} receiveShadow castShadow>
            <boxGeometry args={[1, WALL_E, rows - shelfZ]} />
            {wallMat(walls.side)}
          </mesh>
          <mesh position={[x, WALL_E + 0.04, (shelfZ + rows) / 2]}>
            <boxGeometry args={[1.1, 0.08, rows - shelfZ]} />
            {wallMat(walls.side)}
          </mesh>
        </group>
      ))}
      {/* SOUTH — the drop. The camera trails from here, so it is a
          see-through iron balustrade on a low stone plinth, not a slab:
          a slab under a trailing lens is a bright ledge filling the
          bottom of the screen. The eye goes to the Anima below. */}
      {/* spans between the side walls, not through them — at full width its
          ends were coplanar with the walls' outer faces and flickered */}
      <mesh position={[cols / 2, 0.14, rows - 0.5]} receiveShadow>
        <boxGeometry args={[cols - 1.9, 0.28, 0.5]} />
        {wallMat(walls.s)}
      </mesh>
      {Array.from({ length: Math.floor(cols / 2) + 1 }, (_, i) => (
        <mesh key={i} position={[i * 2, 0.28 + WALL_S / 2, rows - 0.5]}>
          <cylinderGeometry args={[0.035, 0.045, WALL_S, 6]} />
          <meshStandardMaterial {...IRON} />
        </mesh>
      ))}
      {[0.28 + WALL_S, 0.28 + WALL_S * 0.55].map((y, i) => (
        <mesh key={i} position={[cols / 2, y, rows - 0.5]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[i ? 0.02 : 0.035, i ? 0.02 : 0.035, cols, 6]} />
          <meshStandardMaterial {...IRON} />
        </mesh>
      ))}
      {/* the underside — the hall is a slab hanging in the Unstable World */}
      {/* ⚠ top sits 2 cm BELOW the floor. At -1.2 with height 2.4 its top was
          exactly y=0 — the floor plane's height — and the two fought for the
          same depth every frame: the Plaza's big floor flicker (2026-09-22). */}
      <mesh position={[cols / 2, -1.22, rows / 2]}>
        <boxGeometry args={[cols + 0.4, 2.4, rows + 0.4]} />
        <meshStandardMaterial color="#0d0914" roughness={1} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE PORTALS — five arches on the shelf. ONE useFrame spins all five.
// ═══════════════════════════════════════════════════════════════════
// The arch frame: two pillars, a half-torus lintel, a keystone slab
// with the arch's sigil, rune strips down the pillars, and the vortex
// disc. Locked doors (a `locked` string) swirl slower and dimmer — the
// door is painted and lit but shut, and the painting shows it lit.
// `Frame` (optional): draws the stone of one arch instead of the primitives
// below — the Blender arch (portal3D.jsx) passes it; the vortex, maw, halo,
// pool and this ONE useFrame stay here for both, so the doors behave the same.
export function Portals({ portals, shelfZ = SHELF_ROWS, Frame = null }) {
  const discs = useRef([]);
  const halos = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    for (let i = 0; i < discs.current.length; i++) {
      const m = discs.current[i]; if (!m) continue;
      const slow = m.userData.locked ? 0.35 : 1;
      m.material.map.rotation = -t * 0.55 * slow;
      m.material.map.center.set(0.5, 0.5);
      m.material.opacity = 0.92 + Math.sin(t * 2.1 + i) * 0.05;
    }
    for (let i = 0; i < halos.current.length; i++) {
      const h = halos.current[i]; if (!h) continue;
      h.material.opacity = (h.userData.locked ? 0.05 : 0.10) + Math.sin(t * 1.4 + i * 1.7) * 0.03;
      h.scale.setScalar(1 + Math.sin(t * 0.9 + i) * 0.03);
    }
  });
  return (
    <group>
      {portals.map((p, i) => {
        const b = rectBounds(p.zone); if (!b) return null;
        const W = Math.min(b.w * 0.72, 4.4);       // arch width from its zone width
        const H = W * 1.15, R = W / 2, y0 = WALL_N - 0.25 + 0.25; // stands on the shelf slab
        const z = shelfZ - 2.4;                      // centred on the shelf
        const tex = vortexTex(p.color);
        const runes = runeStripTex(p.color);
        return (
          <group key={p.id} position={[b.cx, y0, z]}>
            {Frame ? <Frame p={p} W={W} H={H} /> : (<>
            {/* pillars */}
            {[-1, 1].map((s) => (
              <group key={s} position={[s * (R + 0.18), 0, 0]}>
                <mesh position={[0, (H - R) / 2, 0]} castShadow>
                  <boxGeometry args={[0.5, H - R, 0.5]} />
                  <meshStandardMaterial {...STONE} />
                </mesh>
                <mesh position={[0, (H - R) / 2, 0.26]}>
                  <planeGeometry args={[0.3, H - R - 0.2]} />
                  <meshBasicMaterial map={runes} toneMapped={false} />
                </mesh>
                <mesh position={[0, 0.12, 0]}><boxGeometry args={[0.6, 0.24, 0.66]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
              </group>
            ))}
            {/* lintel — a half torus */}
            <mesh position={[0, H - R, 0]} rotation={[0, 0, 0]} castShadow>
              <torusGeometry args={[R + 0.18, 0.22, 8, 22, Math.PI]} />
              <meshStandardMaterial {...STONE} />
            </mesh>
            {/* keystone with the sigil */}
            <mesh position={[0, H + 0.12, 0]}>
              <boxGeometry args={[0.7, 0.5, 0.6]} />
              <meshStandardMaterial {...DARK_STONE} />
            </mesh>
            <mesh position={[0, H + 0.12, 0.31]}>
              <planeGeometry args={[0.44, 0.44]} />
              <meshBasicMaterial map={sigilTex(p.id, p.color)} transparent toneMapped={false} />
            </mesh>
            </>)}
            {/* the vortex: ONE disc the full height of the opening, centred
                on it, so the swirl has one eye. Its overrun past the arch's
                sides is inside the pillars' depth and under the lintel's
                tube (checked: at W=4.4 the bulge is 0.2, the tube covers
                0.4). Behind it a dark maw fills the bottom corners. */}
            <mesh position={[0, H / 2, -0.01]}>
              <planeGeometry args={[R * 2, H]} />
              <meshBasicMaterial color="#07040f" />
            </mesh>
            <mesh ref={(el) => { discs.current[i] = el; if (el) el.userData.locked = !!p.locked; }} position={[0, H / 2, 0.02]}>
              <circleGeometry args={[H / 2, 32]} />
              <meshBasicMaterial map={tex} transparent toneMapped={false} />
            </mesh>
            {/* the halo that spills out of the door and the pool on the shelf */}
            <mesh ref={(el) => { halos.current[i] = el; if (el) el.userData.locked = !!p.locked; }} position={[0, H / 2, 0.3]}>
              <planeGeometry args={[W * 1.5, H * 1.4]} />
              <meshBasicMaterial color={p.color} transparent opacity={0.1} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 1.4]}>
              <circleGeometry args={[W * 0.7, 20]} />
              <meshBasicMaterial color={p.color} transparent opacity={p.locked ? 0.08 : 0.16} depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE BULLETIN BOARD — off the painting: a round plank board with a
// carved rim, iron studs, on a thick post with a cross-bar, set in a
// heap of pale stones; notices pinned with a wax seal, a key drawn on
// one, a stamped writ.
// ═══════════════════════════════════════════════════════════════════
export const noticeTex = (k) => canvasTex("plaza3d:notice:" + k, 64, 80, (g, w, h) => {
  const R = mulberry32(0xa0 + k);
  g.fillStyle = k % 2 ? "#e8d8b4" : "#d9c9a2"; g.fillRect(0, 0, w, h);
  g.fillStyle = "rgba(90,60,30,0.6)"; for (let y = 14; y < h - 8; y += 9) g.fillRect(8, y, 20 + R() * 28, 2);
  if (k === 0) { g.fillStyle = "#a02030"; g.beginPath(); g.arc(w - 14, h - 14, 7, 0, Math.PI * 2); g.fill(); }
  if (k === 2 || k === 4) { g.strokeStyle = "#4a3a20"; g.lineWidth = 3; g.beginPath(); g.arc(w / 2, 22, 6, 0, Math.PI * 2); g.moveTo(w / 2, 28); g.lineTo(w / 2, 44); g.lineTo(w / 2 + 6, 44); g.stroke(); }
  if (k === 1) { g.strokeStyle = "#c9a84c"; g.lineWidth = 2; g.strokeRect(6, 6, w - 12, 14); }
});
export function BulletinBoard({ rect }) {
  const b = rectBounds(rect); const wood = useMemo(() => woodTex(), []);
  const notices = useMemo(() => [0, 1, 2, 3, 4].map(noticeTex), []);
  const pins = useMemo(() => { const R = mulberry32(0xb0a2d); return Array.from({ length: 5 }, (_, i) => ({ x: Math.cos(i * 1.26 + 0.4) * 0.34 + (R() - 0.5) * 0.1, y: Math.sin(i * 1.26 + 0.4) * 0.3 + (R() - 0.5) * 0.1, r: (R() - 0.5) * 0.4 })); }, []);
  if (!b) return null;
  const S = Math.min(b.w, 3) / 3 * 1.15;
  return (
    <group position={[b.cx, 0, b.cz]} scale={S}>
      {/* the post, the cross-bar, the heap of stones */}
      <mesh position={[0, 0.9, 0]} castShadow><boxGeometry args={[0.2, 1.8, 0.2]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, 2.35, 0]}><boxGeometry args={[0.14, 0.5, 0.14]} /><meshStandardMaterial map={wood} /></mesh>
      {[[0.3, 0.1, 0.12, 0.2], [-0.28, 0.09, 0.16, 0.18], [0.06, 0.1, -0.26, 0.19], [-0.1, 0.06, 0.3, 0.14], [0.22, 0.05, -0.1, 0.13]].map((p, i) => (
        <mesh key={i} position={[p[0], p[1], p[2]]} rotation={[i, i * 0.7, 0]}><dodecahedronGeometry args={[p[3], 0]} /><meshStandardMaterial color="#8c8494" roughness={0.9} /></mesh>
      ))}
      {/* the board: a rim of planks, the studded ring, the face */}
      <mesh position={[0, 1.6, 0]} castShadow><cylinderGeometry args={[0.92, 0.92, 0.14, 24]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, 1.6, 0.08]}><torusGeometry args={[0.8, 0.05, 6, 28]} /><meshStandardMaterial color="#3a2818" roughness={0.9} /></mesh>
      {Array.from({ length: 12 }, (_, i) => { const a = i / 12 * Math.PI * 2; return <mesh key={i} position={[Math.cos(a) * 0.86, 1.6 + Math.sin(a) * 0.86, 0.085]}><sphereGeometry args={[0.03, 6, 5]} /><meshStandardMaterial {...IRON} /></mesh>; })}
      <mesh position={[0, 1.6, 0.075]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.76, 0.76, 0.02, 24]} /><meshStandardMaterial color="#5a3a26" roughness={0.95} /></mesh>
      {/* the notices, each pinned */}
      {pins.map((p, i) => (
        <group key={i} position={[p.x, 1.6 + p.y, 0.095]} rotation={[0, 0, p.r]}>
          <mesh><planeGeometry args={[0.28, 0.35]} /><meshStandardMaterial map={notices[i]} roughness={0.9} /></mesh>
          <mesh position={[0, 0.15, 0.01]}><sphereGeometry args={[0.02, 6, 5]} /><meshStandardMaterial color={i % 2 ? "#c9a84c" : "#a02030"} metalness={0.5} roughness={0.4} /></mesh>
        </group>
      ))}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// WILLY'S BENCH — off the painting: a thick stone table with a carved
// apron of arches and a medallion, a big iron lantern burning warm, the
// open book, a leather pouch, the iron bucket bristling with tools, the
// broom leaning on the end. Willy himself is in plazaFolk3D.
// ═══════════════════════════════════════════════════════════════════
export function WillyBench({ rect }) {
  const b = rectBounds(rect); const wood = useMemo(() => woodTex(), []);
  const paper = useMemo(() => paperTex(), []);
  const flame = useRef();
  useFrame(({ clock }) => { const t = clock.getElapsedTime(); if (flame.current) flame.current.intensity = G(LIGHT.lantern) * (1 + Math.sin(t * 7) * 0.05 + Math.sin(t * 13) * 0.03); });
  if (!b) return null;
  const L = b.w * 0.42;   // the slab is a third of the box — the rest is tools
  const x = b.cx + b.w * 0.12, z = b.cz;
  const PALE = { color: "#9a92a4", roughness: 0.85 };
  return (
    <group position={[x, 0, z]}>
      {/* the slab, the carved apron, the legs with their capitals */}
      <mesh position={[0, 0.78, 0]} castShadow receiveShadow><boxGeometry args={[L, 0.14, 1.0]} /><meshStandardMaterial {...PALE} /></mesh>
      <mesh position={[0, 0.58, 0]} castShadow><boxGeometry args={[L - 0.1, 0.28, 0.9]} /><meshStandardMaterial {...STONE} /></mesh>
      {[-0.5, 0.5].map((sx) => <mesh key={sx} position={[sx * (L * 0.36), 0.52, 0.46]}><torusGeometry args={[0.11, 0.025, 6, 14, Math.PI]} /><meshStandardMaterial {...PALE} /></mesh>)}
      <mesh position={[0, 0.58, 0.47]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.12, 0.12, 0.03, 12]} /><meshStandardMaterial {...PALE} /></mesh>
      {[-1, 1].map((s) => (
        <group key={s} position={[s * (L / 2 - 0.3), 0, 0]}>
          <mesh position={[0, 0.22, 0]} castShadow><boxGeometry args={[0.4, 0.44, 0.7]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
          <mesh position={[0, 0.06, 0]}><boxGeometry args={[0.5, 0.12, 0.8]} /><meshStandardMaterial {...STONE} /></mesh>
        </group>
      ))}
      {/* the open book, the pouch */}
      <group position={[0.15, 0.87, 0.05]} rotation={[0, -0.2, 0]}>
        {[-1, 1].map((s) => <mesh key={s} position={[s * 0.19, 0, 0]} rotation={[0, 0, s * 0.18]}><boxGeometry args={[0.38, 0.03, 0.42]} /><meshStandardMaterial map={paper} /></mesh>)}
        <mesh position={[0, -0.03, 0]}><boxGeometry args={[0.8, 0.02, 0.46]} /><meshStandardMaterial color="#4a3320" /></mesh>
      </group>
      <mesh position={[L / 2 - 0.35, 0.94, -0.15]} scale={[1, 0.7, 0.8]}><sphereGeometry args={[0.13, 10, 8]} /><meshStandardMaterial color="#6b4a2a" roughness={0.9} /></mesh>
      <mesh position={[L / 2 - 0.35, 1.03, -0.15]}><torusGeometry args={[0.05, 0.012, 5, 10]} /><meshStandardMaterial color="#3a2a18" /></mesh>
      {/* the lantern — iron ribs, a glass of warm light, a real light, the only one this side of the room */}
      <group position={[-L / 2 + 0.35, 0.85, -0.1]}>
        <mesh position={[0, 0.02, 0]}><cylinderGeometry args={[0.15, 0.17, 0.05, 8]} /><meshStandardMaterial color="#3b2f22" roughness={0.6} metalness={0.4} /></mesh>
        {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sz], i) => <mesh key={i} position={[sx * 0.11, 0.26, sz * 0.11]}><boxGeometry args={[0.03, 0.46, 0.03]} /><meshStandardMaterial color="#3b2f22" roughness={0.6} metalness={0.4} /></mesh>)}
        <mesh position={[0, 0.26, 0]}><boxGeometry args={[0.2, 0.42, 0.2]} /><meshStandardMaterial color="#ffd27a" emissive="#ffb040" emissiveIntensity={0.9} transparent opacity={0.7} toneMapped={false} /></mesh>
        <mesh position={[0, 0.2, 0]}><sphereGeometry args={[0.05, 8, 6]} /><meshBasicMaterial color="#fff2c0" toneMapped={false} /></mesh>
        <mesh position={[0, 0.52, 0]}><coneGeometry args={[0.18, 0.12, 4]} /><meshStandardMaterial color="#2b2118" /></mesh>
        <mesh position={[0, 0.64, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.05, 0.012, 5, 10]} /><meshStandardMaterial {...IRON} /></mesh>
        <pointLight ref={flame} position={[0, 0.3, 0]} intensity={G(LIGHT.lantern)} distance={6} decay={1.4} color="#ffc46a" />
      </group>
      {/* the iron bucket, bristling with tools */}
      <group position={[-L / 2 - 0.6, 0, 0.05]}>
        <mesh position={[0, 0.3, 0]} castShadow><cylinderGeometry args={[0.27, 0.22, 0.6, 12]} /><meshStandardMaterial color="#4a4652" roughness={0.5} metalness={0.6} /></mesh>
        <mesh position={[0, 0.58, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.27, 0.02, 5, 14]} /><meshStandardMaterial {...IRON} /></mesh>
        {[[-0.08, 0.3, 0.1, "shovel"], [0.1, -0.2, -0.05, "rake"], [-0.12, 0.05, -0.12, "pick"], [0.05, 0.15, 0.14, "hoe"], [0.12, -0.05, 0.08, "shovel"]].map((r, i) => (
          <group key={i} position={[r[0], 0.3, r[2]]} rotation={[r[1], i * 0.8, r[0] * 2]}>
            <mesh position={[0, 0.55, 0]}><cylinderGeometry args={[0.02, 0.02, 1.1, 5]} /><meshStandardMaterial color="#5a4632" /></mesh>
            {r[3] === "shovel" && <mesh position={[0, 1.15, 0]}><boxGeometry args={[0.16, 0.2, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>}
            {r[3] === "rake" && [-0.06, -0.02, 0.02, 0.06].map((tx) => <mesh key={tx} position={[tx, 1.15, 0]}><boxGeometry args={[0.015, 0.14, 0.015]} /><meshStandardMaterial {...IRON} /></mesh>)}
            {r[3] === "pick" && <mesh position={[0, 1.12, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.012, 0.03, 0.34, 5]} /><meshStandardMaterial {...IRON} /></mesh>}
            {r[3] === "hoe" && <mesh position={[0.05, 1.12, 0]} rotation={[0, 0, -0.8]}><boxGeometry args={[0.14, 0.09, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>}
          </group>
        ))}
      </group>
      {/* the broom, leaning on the east end */}
      <group position={[L / 2 + 0.3, 0, 0.1]} rotation={[0, 0, -0.35]}>
        <mesh position={[0, 0.8, 0]}><cylinderGeometry args={[0.022, 0.022, 1.6, 5]} /><meshStandardMaterial color="#6b5236" /></mesh>
        <mesh position={[0, 0.12, 0]} scale={[1, 1, 0.6]}><coneGeometry args={[0.17, 0.4, 8]} /><meshStandardMaterial color="#a88c55" roughness={1} /></mesh>
        <mesh position={[0, 0.3, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.06, 0.012, 5, 10]} /><meshStandardMaterial color="#7d6a4c" /></mesh>
      </group>
      {/* loose pages on the floor */}
      <mesh position={[0.4, 0.01, 0.75]} rotation={[-Math.PI / 2, 0, 0.4]}><planeGeometry args={[0.28, 0.34]} /><meshStandardMaterial map={paper} /></mesh>
      <mesh position={[-0.5, 0.01, 0.8]} rotation={[-Math.PI / 2, 0, -0.3]}><planeGeometry args={[0.28, 0.34]} /><meshStandardMaterial map={paper} /></mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SWEET TREATS — V'yen's booth, off the painting: two carved posts in
// dark cherry wood with finials, a crest of scrollwork over the beam,
// the plank sign hung on chains, strings of red beads and gems hanging
// from the beam like a curtain, a red patterned cloth over the counter
// with a fringe, the ledger, the bottle, the coins, and gems everywhere
// underfoot. V'yen and her coil are in plazaFolk3D; the coil is HER body.
// ═══════════════════════════════════════════════════════════════════
const clothRedTex = () => canvasTex("plaza3d:clothred", 256, 256, (g, w, h) => {
  g.fillStyle = "#7a1424"; g.fillRect(0, 0, w, h);
  g.strokeStyle = "rgba(240,180,90,0.55)"; g.lineWidth = 2;
  for (let y = 16; y < h; y += 48) for (let x = 16; x < w; x += 48) {
    g.beginPath(); g.moveTo(x + 16, y); g.lineTo(x + 32, y + 16); g.lineTo(x + 16, y + 32); g.lineTo(x, y + 16); g.closePath(); g.stroke();
    g.beginPath(); g.arc(x + 16, y + 16, 5, 0, Math.PI * 2); g.stroke();
  }
  g.strokeStyle = "rgba(255,120,150,0.35)"; for (let y = 40; y < h; y += 48) { g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke(); }
});
const CHERRY = { color: "#4a1a1e", roughness: 0.7, metalness: 0.1 };
const CHERRY_L = { color: "#6a2a30", roughness: 0.7, metalness: 0.1 };
function BeadString({ x, z, y0, n = 8, len = 0.7, seed = 0 }) {
  const R = useMemo(() => mulberry32(0x5eed + seed), [seed]);
  const beads = useMemo(() => Array.from({ length: n }, (_, i) => ({ y: y0 - i * (len / n), r: 0.028 + (i % 3 === 2 ? 0.02 : 0), c: i % 3 === 2 ? (R() < 0.5 ? C.nullus : "#ff3b5c") : "#b02030" })), [n, len, y0, R]);
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, y0 - len / 2, 0]}><cylinderGeometry args={[0.006, 0.006, len, 3]} /><meshStandardMaterial color="#2a0a0e" /></mesh>
      {beads.map((b, i) => <mesh key={i} position={[0, b.y, 0]}><sphereGeometry args={[b.r, 6, 5]} /><meshStandardMaterial color={b.c} emissive={b.c} emissiveIntensity={b.r > 0.04 ? 0.6 : 0.1} roughness={0.3} toneMapped={false} /></mesh>)}
    </group>
  );
}
export function SweetTreatsStall({ rects }) {
  const b = rectBounds(rects);
  const sign = useMemo(() => signTex("SWEET\nTREATS", "#f4e8d0", "#7a4a2a", 256, 160, "bold 46px Georgia, serif"), []);
  const cloth = useMemo(() => clothRedTex(), []);
  const gems = useMemo(() => { const R = mulberry32(0x6e5); return Array.from({ length: 14 }, () => ({ x: (R() - 0.5) * 3.4, z: 0.3 + R() * 0.7, r: 0.05 + R() * 0.06, c: R() < 0.45 ? C.nullus : R() < 0.7 ? "#ff3b5c" : "#ffd35c", rot: R() * 3 })); }, []);
  if (!b) return null;
  // the booth sits in the EAST half of the box — sign, counter, V'yen
  const bx = b.x1 + 1 - 2.6, bz = b.y0 + 1.3;
  // ⚑ LIFT (caP'n, 2026-09-23: "make the sign taller just extend it up a bit") — the canopy stands 0.55 m
  // higher so the sign clears the keeper's face from the Plaza camera; the sculpted plaza_stall.glb got
  // the same (builders/plaza_props_blender/build_stall.py). This is its fail-safe: kept in step.
  const LIFT = 0.55;
  return (
    <group position={[bx, 0, bz]}>
      {/* two carved posts at the front, plain ones behind, finials on all */}
      {[[-1.45, 0.95, true], [1.45, 0.95, true], [-1.45, -0.9, false], [1.45, -0.9, false]].map((p, i) => (
        <group key={i} position={[p[0], 0, p[1]]}>
          <mesh position={[0, (3.0 + LIFT) / 2, 0]} castShadow><boxGeometry args={[0.16, 3.0 + LIFT, 0.16]} /><meshStandardMaterial {...CHERRY} /></mesh>
          {p[2] && [0.5, 2.0].map((y) => <mesh key={y} position={[0, y, 0]}><boxGeometry args={[0.24, 0.12, 0.24]} /><meshStandardMaterial {...CHERRY_L} /></mesh>)}
          <mesh position={[0, 3.12 + LIFT, 0]}><sphereGeometry args={[0.1, 8, 6]} /><meshStandardMaterial {...CHERRY_L} /></mesh>
          <mesh position={[0, 3.28 + LIFT, 0]}><octahedronGeometry args={[0.07, 0]} /><meshStandardMaterial color="#ff3b5c" emissive="#ff3b5c" emissiveIntensity={0.7} toneMapped={false} /></mesh>
        </group>
      ))}
      {/* the beams, the crest of scrollwork on the front one */}
      <mesh position={[0, 3.0 + LIFT, 0.95]} castShadow><boxGeometry args={[3.1, 0.18, 0.2]} /><meshStandardMaterial {...CHERRY} /></mesh>
      <mesh position={[0, 3.0 + LIFT, -0.9]}><boxGeometry args={[3.1, 0.14, 0.16]} /><meshStandardMaterial {...CHERRY} /></mesh>
      {[-1.45, 1.45].map((x) => <mesh key={x} position={[x, 3.0 + LIFT, 0]}><boxGeometry args={[0.14, 0.14, 1.9]} /><meshStandardMaterial {...CHERRY} /></mesh>)}
      <mesh position={[0, 3.12 + LIFT, 0.95]}><boxGeometry args={[2.4, 0.08, 0.16]} /><meshStandardMaterial {...CHERRY_L} /></mesh>
      {[-1, 1].map((sx) => (
        <group key={sx} position={[sx * 0.95, 3.18 + LIFT, 0.95]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.18, 0.04, 6, 14, Math.PI * 1.5]} /><meshStandardMaterial {...CHERRY_L} /></mesh>
          <mesh position={[sx * 0.3, 0.05, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.1, 0.035, 6, 12, Math.PI * 1.6]} /><meshStandardMaterial {...CHERRY_L} /></mesh>
        </group>
      ))}
      <mesh position={[0, 3.28 + LIFT, 0.95]}><octahedronGeometry args={[0.1, 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.8} toneMapped={false} /></mesh>
      {/* the plank sign, hung flush under the front beam on two short links, a hair in front of it */}
      {[-0.6, 0.6].map((x) => <mesh key={x} position={[x, 2.905 + LIFT, 1.07]}><cylinderGeometry args={[0.01, 0.01, 0.04, 4]} /><meshStandardMaterial {...IRON} /></mesh>)}
      <mesh position={[0, 2.60 + LIFT, 1.08]} rotation={[0.04, 0, 0.03]}><boxGeometry args={[1.7, 0.6, 0.06]} /><meshStandardMaterial color="#7a4a2a" roughness={0.9} /></mesh>
      <mesh position={[0, 2.60 + LIFT, 1.115]} rotation={[0.04, 0, 0.03]}><planeGeometry args={[1.6, 0.52]} /><meshBasicMaterial map={sign} toneMapped={false} /></mesh>
      {/* the bead curtain hanging from the front beam and the side beams */}
      {Array.from({ length: 9 }, (_, i) => <BeadString key={"f" + i} x={-1.3 + i * 0.325} z={0.93} y0={2.9 + LIFT} n={7} len={0.42 + (i % 2) * 0.18} seed={i} />)}
      {Array.from({ length: 4 }, (_, i) => <BeadString key={"l" + i} x={-1.42} z={-0.6 + i * 0.4} y0={2.9 + LIFT} n={9} len={0.7} seed={20 + i} />)}
      {Array.from({ length: 4 }, (_, i) => <BeadString key={"r" + i} x={1.42} z={-0.6 + i * 0.4} y0={2.9 + LIFT} n={9} len={0.7} seed={30 + i} />)}
      {/* the counter, carved, with the patterned cloth draped over it and a fringe */}
      <mesh position={[0, 0.5, 0.55]} castShadow receiveShadow><boxGeometry args={[3.0, 1.0, 0.8]} /><meshStandardMaterial {...CHERRY} /></mesh>
      {[-1.0, 0, 1.0].map((x) => <mesh key={x} position={[x, 0.5, 0.96]}><boxGeometry args={[0.7, 0.6, 0.03]} /><meshStandardMaterial {...CHERRY_L} /></mesh>)}
      <mesh position={[0, 1.03, 0.55]}><boxGeometry args={[3.1, 0.05, 0.9]} /><meshStandardMaterial map={cloth} roughness={0.95} /></mesh>
      <mesh position={[0, 0.72, 1.0]}><boxGeometry args={[2.4, 0.62, 0.02]} /><meshStandardMaterial map={cloth} roughness={0.95} /></mesh>
      {Array.from({ length: 12 }, (_, i) => <mesh key={i} position={[-1.1 + i * 0.2, 0.36, 1.0]}><cylinderGeometry args={[0.012, 0.012, 0.14, 3]} /><meshStandardMaterial color="#c9a84c" /></mesh>)}
      {/* the ledger, open; the bottle; the coins; a gem dish */}
      <group position={[0.2, 1.07, 0.6]}>
        {[-1, 1].map((s) => <mesh key={s} position={[s * 0.17, 0, 0]} rotation={[0, 0, s * 0.15]}><boxGeometry args={[0.34, 0.03, 0.4]} /><meshStandardMaterial color="#e2d3a8" /></mesh>)}
        <mesh position={[0, -0.02, 0]}><boxGeometry args={[0.7, 0.03, 0.44]} /><meshStandardMaterial color="#3a1a10" /></mesh>
      </group>
      <mesh position={[-0.95, 1.24, 0.5]}><cylinderGeometry args={[0.08, 0.1, 0.34, 8]} /><meshStandardMaterial color="#d04030" transparent opacity={0.85} roughness={0.2} /></mesh>
      <mesh position={[-0.95, 1.45, 0.5]}><cylinderGeometry args={[0.03, 0.03, 0.1, 6]} /><meshStandardMaterial color="#5a3a20" /></mesh>
      {Array.from({ length: 9 }, (_, i) => (
        <mesh key={i} position={[0.8 + (i % 3) * 0.13, 1.06 + ((i / 3) | 0) * 0.012, 0.35 + ((i / 3) | 0) * 0.14]} rotation={[-Math.PI / 2, 0, i]}><circleGeometry args={[0.06, 10]} /><meshStandardMaterial {...BRASS} /></mesh>
      ))}
      <group position={[-0.72, 1.06, 0.30]}>{/* moved off her right hand (the sculpt's rig, 2026-09-23) */}
        {[[0, 0, 0, 0.08, C.nullus], [0.09, 0, 0.05, 0.06, "#ff3b5c"], [-0.08, 0, 0.06, 0.05, "#ffd35c"]].map((g, k) => <mesh key={k} position={[g[0], g[3] * 0.7, g[2]]} rotation={[0.3 * k, k, 0.2]}><octahedronGeometry args={[g[3], 0]} /><meshStandardMaterial color={g[4]} emissive={g[4]} emissiveIntensity={0.7} roughness={0.15} toneMapped={false} /></mesh>)}
      </group>
      {/* gems scattered on the ground in front — hers, off the coil */}
      {gems.map((g, i) => (
        <mesh key={i} position={[g.x, g.r, 1.1 + g.z]} rotation={[g.rot, g.rot * 0.7, 0]}>
          <octahedronGeometry args={[g.r, 0]} />
          <meshStandardMaterial color={g.c} emissive={g.c} emissiveIntensity={0.7} roughness={0.2} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// V'AEL'S PLAZA FOUNTAIN — the octagonal basin, the liquid N'ullus, the
// four ram heads, the crowned ram with the crescent, the jets, the
// gems. ONE useFrame: the water scrolls, the jets shimmer, the gems
// breathe. The point light is the room's key glow.
// ═══════════════════════════════════════════════════════════════════
// A ram, in stone — off the painting: a long-muzzled head, a heavy brow,
// the great horns spiralling forward and down, eyes lit N'ullus, and on
// the one at the top a circlet with the crescent. The horns are the
// same spiral V'ael wears (plazaFolk3D's RamHorn), in stone and THICK —
// in the painting a horn is as fat as the muzzle, not a wire.
const FSTONE = { color: "#8a8196", roughness: 0.8, metalness: 0.05 };   // the fountain's grey-lilac stone
const FSTONE_D = { color: "#4e4658", roughness: 0.9 };
const stoneHornMat = <meshStandardMaterial color="#9a90a6" roughness={0.7} />;
function RamHead({ scale = 1, crown = false }) {
  const R = 0.24;
  return (
    <group scale={scale}>
      <mesh castShadow scale={[1, 1.1, 1.2]}><sphereGeometry args={[R, 14, 12]} /><meshStandardMaterial {...FSTONE} /></mesh>
      {/* the long muzzle, the nose, the brow ridge */}
      <mesh position={[0, -R * 0.5, R * 1.0]} rotation={[0.4, 0, 0]} scale={[0.82, 0.8, 1.5]}><sphereGeometry args={[R * 0.62, 12, 10]} /><meshStandardMaterial {...FSTONE} /></mesh>
      <mesh position={[0, -R * 0.75, R * 1.8]}><sphereGeometry args={[R * 0.2, 8, 6]} /><meshStandardMaterial {...FSTONE_D} /></mesh>
      <mesh position={[0, R * 0.45, R * 0.75]} scale={[1.5, 0.35, 0.6]}><sphereGeometry args={[R * 0.5, 10, 8]} /><meshStandardMaterial {...FSTONE} /></mesh>
      {/* the ears, laid back under the horns */}
      {[-1, 1].map((s) => <mesh key={s} position={[s * R * 1.0, R * 0.05, -R * 0.15]} rotation={[0.3, 0, s * 1.3]} scale={[0.45, 1, 0.7]}><coneGeometry args={[R * 0.32, R * 0.8, 6]} /><meshStandardMaterial {...FSTONE} /></mesh>)}
      {/* THE HORNS — big, and fat */}
      {[-1, 1].map((s) => <RamHorn key={s} s={s} R0={R * 1.6} base={[s * R * 0.8, R * 0.55, -R * 0.15]} splay={0.6} thick={1.7} mat={stoneHornMat} />)}
      {/* eyes — glowing N'ullus, no light */}
      {[-1, 1].map((s) => <mesh key={s} position={[s * R * 0.52, R * 0.12, R * 0.85]}><sphereGeometry args={[R * 0.11, 8, 6]} /><meshBasicMaterial color={C.nullus} toneMapped={false} /></mesh>)}
      {crown && (
        <group position={[0, R * 1.1, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[R * 0.72, R * 0.08, 6, 20]} /><meshStandardMaterial color="#c9b8e0" emissive={C.nullus} emissiveIntensity={0.4} toneMapped={false} /></mesh>
          {/* the crescent, standing on the circlet, horns up */}
          <mesh position={[0, R * 0.6, 0]} rotation={[0, 0, Math.PI * 0.5]}><torusGeometry args={[R * 0.45, R * 0.09, 8, 24, Math.PI * 1.25]} /><meshStandardMaterial color="#e8dcf8" emissive={C.nullus} emissiveIntensity={0.8} toneMapped={false} /></mesh>
        </group>
      )}
    </group>
  );
}
// a strip of carved runes for the basin wall
export const runeBandTex = () => canvasTex("plaza3d:runeband", 1024, 64, (g, w, h) => {
  const R = mulberry32(0x1e4e);
  g.fillStyle = "#6e657a"; g.fillRect(0, 0, w, h);
  g.strokeStyle = "rgba(220,200,255,0.75)"; g.lineWidth = 3; g.lineCap = "round"; g.shadowColor = "#B388FF"; g.shadowBlur = 5;
  for (let x = 14; x < w - 10; x += 28) {
    g.beginPath(); for (let k = 0; k < 4; k++) { const xx = x + R() * 16, yy = 12 + R() * 40; if (k === 0) g.moveTo(xx, yy); else g.lineTo(xx, yy); } g.stroke();
  }
});
// a fall of water: a near-vertical tube from a mouth to the pool,
// leaning outward a hair as it drops (the painting's streams do)
function fallCurve(from, to) {
  const mid = new THREE.Vector3(from[0] + (to[0] - from[0]) * 0.35, from[1] - (from[1] - to[1]) * 0.45, from[2] + (to[2] - from[2]) * 0.35);
  return new THREE.QuadraticBezierCurve3(new THREE.Vector3(...from), mid, new THREE.Vector3(...to));
}
const WATER = { color: "#3a2290", emissive: "#2c1488", emissiveIntensity: 0.6, roughness: 0.15, metalness: 0.1, transparent: true, opacity: 0.95, toneMapped: false };
export function Fountain({ rect, plaque = "V'ael's Plaza" }) {
  const b = rectBounds(rect);
  const water = useRef(); const jets = useRef([]); const gems = useRef([]); const light = useRef();
  const wtex = useMemo(() => { const t = waterTex(); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t; }, []);
  const sign = useMemo(() => signTex(plaque, "#f4e6c8", "#4a1a24", 512, 128, "bold 60px Georgia, serif"), [plaque]);
  // THE ELLIPSE. The table's box is 10 wide × 4 deep because the painting
  // looks down on the basin; in the round it would spill two tiles into
  // the ring on both sides. So the basin is an ellipse the width of the
  // box and a hair deeper than it, and the pedestal in the middle stays
  // round. Every basin mesh carries [ex, 1, ez]; the gems are computed
  // on the same ellipse.
  const E = useMemo(() => b ? { ex: (b.w * 0.98) / 9.8, ez: (b.d + 1.3) / 9.8, sy: 0.85 } : null, [b]);
  const gemRows = useMemo(() => { const R = mulberry32(0x9e3); return Array.from({ length: N_GEMS }, (_, i) => ({ a: i / N_GEMS * Math.PI * 2 + 0.2, s: 0.08 + R() * 0.07, ph: R() * 6 })); }, []);
  // THE WATER, as the painting pours it: every stream comes out of a
  // ram's MOUTH and falls — the four corner rams into the pool, the four
  // rim rams into the pool over the bowl's edge, the bowl itself over its
  // south lip, and the top ram's two streams into the bowl. Pedestal
  // space (the group below is scaled sy).
  const jetGeo = useMemo(() => {
    if (!E) return [];
    const out = [];
    const Y_POOL = 0.92;
    for (let i = 0; i < 4; i++) {                       // corner rams
      const a = i * Math.PI / 2 + Math.PI / 4;
      out.push(new THREE.TubeGeometry(fallCurve([Math.cos(a) * 2.05, 1.42, Math.sin(a) * 2.05], [Math.cos(a) * 2.5, Y_POOL, Math.sin(a) * 2.5]), 12, 0.06, 7, false));
    }
    for (let i = 0; i < 4; i++) {                       // rim rams
      const a = i * Math.PI / 2;
      out.push(new THREE.TubeGeometry(fallCurve([Math.cos(a) * 1.9, 2.38, Math.sin(a) * 1.9], [Math.cos(a) * 2.9, Y_POOL, Math.sin(a) * 2.9]), 14, 0.06, 7, false));
    }
    out.push(new THREE.TubeGeometry(fallCurve([0, 2.32, 1.45], [0, Y_POOL, 2.1]), 12, 0.09, 7, false));   // the bowl's spill
    for (const s of [-1, 1]) {                          // the crowned ram, into the bowl
      out.push(new THREE.TubeGeometry(fallCurve([s * 0.35, 3.35, 0.55], [s * 1.0, 2.4, 0.6]), 10, 0.045, 6, false));
    }
    return out;
  }, [E]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (water.current) { wtex.offset.set(t * 0.03, -t * 0.045); water.current.material.emissiveIntensity = 0.6 + Math.sin(t * 1.3) * 0.12; }
    for (let i = 0; i < jets.current.length; i++) { const j = jets.current[i]; if (j) j.material.opacity = 0.7 + Math.sin(t * 6 + i * 1.1) * 0.15; }
    for (let i = 0; i < gems.current.length; i++) { const g = gems.current[i]; if (g) g.material.emissiveIntensity = 0.7 + Math.sin(t * 1.7 + gemRows[i].ph) * 0.35; }
    if (light.current) light.current.intensity = G(LIGHT.fountain) * (1 + Math.sin(t * 1.3) * 0.08);
  });
  if (!b || !E) return null;
  const { ex, ez, sy } = E;
  const BASIN = [ex, sy, ez];
  const gemPile = (k) => [[0, 0, 0, 0.11, "#ff5fb0"], [0.12, 0, 0.05, 0.08, "#ffd35c"], [-0.1, 0, 0.07, 0.07, C.nullus], [0.03, 0, -0.1, 0.06, "#ffd35c"]].map((g, i) => (
    <mesh key={k + ":" + i} position={[g[0], g[3] * 0.7, g[2]]} rotation={[0.3 * i, i * 1.3, 0.2]}><octahedronGeometry args={[g[3], 0]} /><meshStandardMaterial color={g[4]} emissive={g[4]} emissiveIntensity={0.7} roughness={0.15} toneMapped={false} /></mesh>
  ));
  return (
    <group position={[b.cx, 0, b.cz]}>
      {/* ── THE OCTAGON: the step, the tall carved wall, the rim ── */}
      <mesh position={[0, 0.12 * sy, 0]} scale={BASIN} receiveShadow castShadow><cylinderGeometry args={[5.15, 5.35, 0.24, 8]} /><meshStandardMaterial {...FSTONE_D} /></mesh>
      <mesh position={[0, 0.55 * sy, 0]} scale={BASIN} receiveShadow castShadow><cylinderGeometry args={[4.62, 4.8, 0.66, 8]} /><meshStandardMaterial {...FSTONE} /></mesh>
      {/* (the rune band that wrapped this wall is gone — caP'n, 2026-09-23: no glyphs on the fountain) */}
      <mesh position={[0, 0.9 * sy, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[ex, ez, sy]}><torusGeometry args={[4.66, 0.17, 6, 8]} /><meshStandardMaterial color="#9c93a8" roughness={0.6} metalness={0.15} /></mesh>
      {/* crescents carved on the four cardinal faces, gem clusters on the corners of the step */}
      {Array.from({ length: 4 }, (_, i) => { const a = i * Math.PI / 2; return <mesh key={i} position={[Math.cos(a) * 4.84 * ex, 0.72 * sy, Math.sin(a) * 4.84 * ez]} rotation={[0, -a + Math.PI / 2, Math.PI * 0.5]}><torusGeometry args={[0.13, 0.03, 6, 16, Math.PI * 1.25]} /><meshBasicMaterial color={C.nullus} toneMapped={false} /></mesh>; })}
      {Array.from({ length: 4 }, (_, i) => { const a = i * Math.PI / 2 + Math.PI / 4; return (
        <group key={i} position={[Math.cos(a) * 5.3 * ex, 0.24 * sy, Math.sin(a) * 5.3 * ez]}>
          {[[0, 0, 0, 0.16], [0.14, -0.04, 0.06, 0.1], [-0.1, -0.05, 0.1, 0.09]].map((g, k) => <mesh key={k} position={[g[0], g[1] + g[3] * 0.8, g[2]]} rotation={[0.2 * k, k, 0.1]}><octahedronGeometry args={[g[3], 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.9} roughness={0.15} toneMapped={false} /></mesh>)}
        </group>); })}
      {/* the pool, deep indigo, a hand below the rim */}
      <mesh ref={water} position={[0, 0.78 * sy, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[ex, ez, 1]}>
        <circleGeometry args={[4.6, 8]} />
        <meshStandardMaterial map={wtex} {...WATER} />
      </mesh>
      {/* ── THE PEDESTAL is ROUND — the heads must not be squashed ── */}
      <group scale={[sy, sy, sy]}>
        {/* the square plinth the corner rams ride, faces to the cardinals */}
        <mesh position={[0, 1.35, 0]} rotation={[0, Math.PI / 4, 0]} castShadow><cylinderGeometry args={[1.95, 2.1, 1.1, 4]} /><meshStandardMaterial {...FSTONE} /></mesh>
        <mesh position={[0, 1.92, 0]} rotation={[0, Math.PI / 4, 0]}><cylinderGeometry args={[2.05, 1.95, 0.12, 4]} /><meshStandardMaterial {...FSTONE_D} /></mesh>
        {/* the plaque on the plinth's south face */}
        <mesh position={[0, 1.3, 1.46]}><planeGeometry args={[1.8, 0.46]} /><meshBasicMaterial map={sign} toneMapped={false} /></mesh>
        {/* FOUR CORNER RAMS, big, facing out, horns as fat as their muzzles */}
        {Array.from({ length: 4 }, (_, i) => {
          const a = i * Math.PI / 2 + Math.PI / 4;
          return <group key={i} position={[Math.cos(a) * 1.55, 1.6, Math.sin(a) * 1.55]} rotation={[0, -a + Math.PI / 2, 0]}><RamHead scale={1.05} /></group>;
        })}
        {/* the bowl: a wide basin on a short stem, water inside, gem piles and rim rams on its lip */}
        <mesh position={[0, 2.0, 0]} castShadow><cylinderGeometry args={[0.75, 0.9, 0.2, 8]} /><meshStandardMaterial {...FSTONE_D} /></mesh>
        <mesh position={[0, 2.2, 0]} castShadow><cylinderGeometry args={[1.6, 0.85, 0.42, 8]} /><meshStandardMaterial {...FSTONE} /></mesh>
        <mesh position={[0, 2.41, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[1.58, 0.08, 6, 8]} /><meshStandardMaterial color="#9c93a8" roughness={0.6} metalness={0.15} /></mesh>
        <mesh position={[0, 2.4, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[1.5, 8]} /><meshStandardMaterial map={wtex} {...WATER} /></mesh>
        {Array.from({ length: 4 }, (_, i) => { const a = i * Math.PI / 2 + Math.PI / 4; return <group key={i} position={[Math.cos(a) * 1.25, 2.4, Math.sin(a) * 1.25]}>{gemPile(i)}</group>; })}
        {/* rim rams at the cardinals, smaller, pouring over the lip */}
        {Array.from({ length: 4 }, (_, i) => {
          const a = i * Math.PI / 2;
          return <group key={i} position={[Math.cos(a) * 1.5, 2.6, Math.sin(a) * 1.5]} rotation={[0, -a + Math.PI / 2, 0]}><RamHead scale={0.62} /></group>;
        })}
        {/* red jewel tassels hung under the bowl's lip */}
        {Array.from({ length: 8 }, (_, i) => { const a = i * Math.PI / 4 + Math.PI / 8; return (
          <group key={i} position={[Math.cos(a) * 1.55, 2.3, Math.sin(a) * 1.55]}>
            <mesh><cylinderGeometry args={[0.012, 0.012, 0.2, 4]} /><meshStandardMaterial color="#8a2a2a" /></mesh>
            <mesh position={[0, -0.14, 0]}><octahedronGeometry args={[0.065, 0]} /><meshStandardMaterial color="#e8453c" emissive="#e8453c" emissiveIntensity={0.9} roughness={0.2} toneMapped={false} /></mesh>
          </group>); })}
        {/* the small drum and THE CROWNED RAM, larger than life, facing south (its BEREPETH plaque is gone —
            caP'n, 2026-09-23: the only words on the fountain are "V'ael's Plaza") */}
        <mesh position={[0, 2.8, 0]} castShadow><cylinderGeometry args={[0.72, 0.82, 0.7, 8]} /><meshStandardMaterial {...FSTONE} /></mesh>
        <mesh position={[0, 3.16, 0]}><cylinderGeometry args={[0.8, 0.72, 0.12, 8]} /><meshStandardMaterial {...FSTONE_D} /></mesh>
        <group position={[0, 3.6, 0]}><RamHead scale={1.7} crown /></group>
        {/* the falls, with a bright core */}
        {jetGeo.map((geo, i) => (
          <group key={i}>
            <mesh ref={(el) => { jets.current[i] = el; }} geometry={geo}><meshBasicMaterial color="#c4a6ff" transparent opacity={0.7} depthWrite={false} toneMapped={false} /></mesh>
            <mesh geometry={geo} scale={[0.998, 0.998, 0.998]}><meshBasicMaterial color="#f4ecff" transparent opacity={0.4} depthWrite={false} toneMapped={false} /></mesh>
          </group>
        ))}
        {/* ripples where the falls land */}
        {[[2.5, Math.PI / 4, 4], [2.9, 0, 4]].flatMap(([r, off, n]) => Array.from({ length: n }, (_, i) => { const a = i * Math.PI / 2 + off; return <mesh key={r + ":" + i} position={[Math.cos(a) * r, 0.93, Math.sin(a) * r]} rotation={[-Math.PI / 2, 0, 0]}><ringGeometry args={[0.16, 0.36, 14]} /><meshBasicMaterial color="#dcc8ff" transparent opacity={0.35} depthWrite={false} toneMapped={false} /></mesh>; }))}
        {/* coins on the bowl's water */}
        {Array.from({ length: 10 }, (_, i) => { const a = i / 10 * Math.PI * 2 + 0.3; return <mesh key={i} position={[Math.cos(a) * 0.9, 2.405, Math.sin(a) * 0.9]} rotation={[-Math.PI / 2, 0, a]}><circleGeometry args={[0.05, 8]} /><meshStandardMaterial {...BRASS} /></mesh>; })}
        {/* the glow that lights the plaza — ONE light */}
        <pointLight ref={light} position={[0, 2.6, 0]} intensity={G(LIGHT.fountain)} distance={22} decay={1.4} color="#bca4ff" castShadow={false} />
      </group>
      {/* gems on the rim, on the ellipse */}
      {gemRows.map((g, i) => (
        <mesh key={i} ref={(el) => { gems.current[i] = el; }} position={[Math.cos(g.a) * 4.7 * ex, 1.0 * sy, Math.sin(g.a) * 4.7 * ez]} rotation={[0.3, g.a, 0]}>
          <octahedronGeometry args={[g.s * 0.9, 0]} />
          <meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.8} roughness={0.15} toneMapped={false} />
        </mesh>
      ))}
      {/* the pool of light on the stones */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]} scale={[ex, ez, 1]}>
        <ringGeometry args={[5.35, 7.4, 8]} />
        <meshBasicMaterial color={C.nullus} transparent opacity={0.07} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VENUS'S CARD PACKS — the kiosk, off the painting: a stone alcove with
// a rune-carved lintel and timber framing, shelves of packs on the back
// wall, two framed cards glowing under glass, the wide glass case at the
// front with three cards stood in it, the paper sign, three candles and
// a hanging key. Venus is in plazaFolk3D.
// ═══════════════════════════════════════════════════════════════════
export const cardFaceTex = (hex) => canvasTex("plaza3d:card:" + hex, 64, 96, (g, w, h) => {
  g.fillStyle = "#1a1020"; g.fillRect(0, 0, w, h);
  g.fillStyle = hex; g.fillRect(5, 5, w - 10, h * 0.55);
  g.strokeStyle = "#c9a84c"; g.lineWidth = 2; g.strokeRect(3, 3, w - 6, h - 6);
  g.fillStyle = "#e8dcc8"; g.fillRect(8, h * 0.66, w - 16, 3); g.fillRect(8, h * 0.74, w - 22, 3); g.fillRect(8, h * 0.82, w - 26, 3);
});
export function PackKiosk({ rect }) {
  const b = rectBounds(rect); const wood = useMemo(() => woodTex("plaza3d:darkwood", 20, 0x99), []);
  const sign = useMemo(() => signTex("VENUS'S\nCARD PACKS\nBUY A PACK!", "#3a2a1a", "#e8d8b0", 256, 256, "bold 34px Georgia, serif"), []);
  const runes = useMemo(() => { const t = runeBandTex(); t.wrapS = THREE.RepeatWrapping; t.repeat.set(2, 1); return t; }, []);
  const faces = useMemo(() => ["#c9a24c", "#7c3a9c", "#2e6a9a"].map(cardFaceTex), []);
  const packs = useMemo(() => {
    const R = mulberry32(0xca4d);
    const cols = ["#a94a2a", "#2e6a9a", "#7c3a9c", "#3c8a4a", "#c9a24c", "#8a2a3a", "#4a4a9c", "#2a8a8a"];
    return Array.from({ length: 16 }, (_, i) => ({ x: -1.35 + (i % 8) * 0.3, y: 1.7 + ((i / 8) | 0) * 0.5, c: cols[(R() * cols.length) | 0], g: R() < 0.35 }));
  }, []);
  const flames = useRef([]);
  useFrame(({ clock }) => { const t = clock.getElapsedTime(); for (let i = 0; i < flames.current.length; i++) { const f = flames.current[i]; if (f) f.scale.setScalar(0.85 + Math.sin(t * 9 + i * 2) * 0.15 + Math.sin(t * 17 + i) * 0.08); } });
  if (!b) return null;
  // 0.8: the painting puts the case at Venus's hip and the shelves at her
  // shoulder — the kiosk is built to HER, not to the box
  const turn = b.d > b.w;   // taller than wide = turned onto the west wall, facing east (2026-09-22)
  const S = Math.min(turn ? b.d : b.w, 6) / 6 * 0.8;
  const W = 3.4, D = (turn ? b.w : b.d) * 0.9 / 0.8;
  const BEAM = { color: "#1e1418", roughness: 0.8 };
  return (
    <group position={[b.cx, 0, b.cz]} rotation={[0, turn ? Math.PI / 2 : 0, 0]} scale={S}>
      {/* the stone alcove: two piers, the lintel with its runes */}
      {[-1, 1].map((sx) => <mesh key={sx} position={[sx * (W / 2 + 0.3), 1.5, -D / 2 + 0.3]} castShadow><boxGeometry args={[0.4, 3.0, 0.7]} /><meshStandardMaterial {...DARK_STONE} /></mesh>)}
      <mesh position={[0, 3.1, -D / 2 + 0.3]} castShadow><boxGeometry args={[W + 1.0, 0.5, 0.75]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
      <mesh position={[0, 3.1, -D / 2 + 0.68]}><planeGeometry args={[W + 0.9, 0.3]} /><meshStandardMaterial map={runes} roughness={0.8} /></mesh>
      {/* the timber frame inside the stone */}
      <mesh position={[0, 1.3, -D / 2 + 0.15]} castShadow><boxGeometry args={[W, 2.6, 0.3]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, 2.72, -D / 2 + 0.34]}><boxGeometry args={[W, 0.16, 0.12]} /><meshStandardMaterial {...BEAM} /></mesh>
      {[-1, 1].map((sx) => <mesh key={sx} position={[sx * (W / 2 - 0.08), 1.3, -D / 2 + 0.34]}><boxGeometry args={[0.14, 2.6, 0.12]} /><meshStandardMaterial {...BEAM} /></mesh>)}
      <mesh position={[0, 2.45, -D / 2 + 0.34]} rotation={[0, 0, 0.35]}><boxGeometry args={[1.6, 0.08, 0.1]} /><meshStandardMaterial {...BEAM} /></mesh>
      <mesh position={[0, 2.45, -D / 2 + 0.34]} rotation={[0, 0, -0.35]}><boxGeometry args={[1.6, 0.08, 0.1]} /><meshStandardMaterial {...BEAM} /></mesh>
      {/* the shelves of packs */}
      {[1.45, 1.95].map((y, i) => <mesh key={i} position={[-0.3, y, -D / 2 + 0.42]}><boxGeometry args={[W - 1.0, 0.05, 0.3]} /><meshStandardMaterial color="#2a1a10" /></mesh>)}
      {packs.map((p, i) => i % 8 < 6 && (
        <mesh key={i} position={[p.x, p.y, -D / 2 + 0.42]} rotation={[-0.1, 0, 0]}>
          <boxGeometry args={[0.26, 0.4, 0.06]} />
          <meshStandardMaterial color={p.c} emissive={p.g ? p.c : "#000"} emissiveIntensity={p.g ? 0.6 : 0} roughness={0.4} toneMapped={!p.g} />
        </mesh>
      ))}
      {/* two framed cards glowing under glass, top right */}
      {[["#ff7a2a", 1.0], ["#4ad8ff", 1.5]].map(([hex, x], i) => (
        <group key={i} position={[x, 2.05, -D / 2 + 0.36]}>
          <mesh><boxGeometry args={[0.34, 0.44, 0.06]} /><meshStandardMaterial color="#3a2a20" roughness={0.6} /></mesh>
          <mesh position={[0, 0, 0.035]}><planeGeometry args={[0.24, 0.34]} /><meshBasicMaterial color={hex} toneMapped={false} /></mesh>
          <mesh position={[0, 0, 0.045]}><planeGeometry args={[0.28, 0.38]} /><meshStandardMaterial color="#9fb8d8" transparent opacity={0.25} roughness={0.05} /></mesh>
        </group>
      ))}
      {/* the paper sign, pinned by the case */}
      <mesh position={[1.35, 1.32, D / 2 - 0.62]} rotation={[0, -0.2, 0.04]}><planeGeometry args={[0.5, 0.5]} /><meshBasicMaterial map={sign} toneMapped={false} side={THREE.DoubleSide} /></mesh>
      {/* the glass case at the front, three cards stood in it */}
      <mesh position={[0, 0.45, D / 2 - 0.5]} castShadow receiveShadow><boxGeometry args={[W, 0.9, 0.9]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, 0.92, D / 2 - 0.5]}><boxGeometry args={[W - 0.1, 0.04, 0.9]} /><meshStandardMaterial color="#2a1a10" /></mesh>
      <mesh position={[0, 1.2, D / 2 - 0.5]}><boxGeometry args={[W - 0.3, 0.5, 0.8]} /><meshStandardMaterial color="#9fb8d8" transparent opacity={0.22} roughness={0.05} depthWrite={false} /></mesh>
      {[-0.9, 0, 0.9].map((x, i) => (
        <mesh key={i} position={[x, 1.2, D / 2 - 0.55]} rotation={[-0.3, 0, 0]}><planeGeometry args={[0.36, 0.5]} /><meshStandardMaterial map={faces[i]} emissive="#ffffff" emissiveMap={faces[i]} emissiveIntensity={0.35} side={THREE.DoubleSide} /></mesh>
      ))}
      {/* three candles, and the key on its hook */}
      {[[1.25, 0.22], [1.45, 0.3], [1.62, 0.18]].map(([x, hgt], i) => (
        <group key={i} position={[x, 0.95, D / 2 - 0.22]}>
          <mesh position={[0, hgt / 2, 0]}><cylinderGeometry args={[0.04, 0.05, hgt, 7]} /><meshStandardMaterial color="#efe3c8" /></mesh>
          <mesh ref={(el) => { flames.current[i] = el; }} position={[0, hgt + 0.05, 0]}><sphereGeometry args={[0.035, 6, 6]} /><meshBasicMaterial color="#ffd27a" toneMapped={false} /></mesh>
          <mesh position={[0, hgt + 0.05, 0]}><sphereGeometry args={[0.1, 8, 6]} /><meshBasicMaterial color="#ffb040" transparent opacity={0.12} depthWrite={false} toneMapped={false} /></mesh>
        </group>
      ))}
      <group position={[W / 2 + 0.1, 0.7, D / 2 - 0.1]}>
        <mesh position={[0, 0.05, 0]} rotation={[0, 0, 0.2]}><torusGeometry args={[0.06, 0.015, 5, 10]} /><meshStandardMaterial {...BRASS} /></mesh>
        <mesh position={[0, -0.12, 0]}><boxGeometry args={[0.025, 0.24, 0.015]} /><meshStandardMaterial {...BRASS} /></mesh>
        <mesh position={[0.03, -0.22, 0]}><boxGeometry args={[0.06, 0.03, 0.015]} /><meshStandardMaterial {...BRASS} /></mesh>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// WILLIE'S WORKSHOP — the tattered tent. Off the painting: a LOW, WIDE
// bell of patched canvas, sagging between its ridges, the hem scalloped
// and pegged out on ropes all round, two leaning poles at the mouth, and
// inside — the forge basin of blue-white fire, the bench, crates of
// N'ullus crystal, the anvil, the rack. The mouth opens SOUTH-WEST,
// exactly where the table walks you in (the west approach column and
// the inside rows 17-18). ONE useFrame for the forge breath.
// ═══════════════════════════════════════════════════════════════════
// The canopy is a hand-built surface, not a cone: a cone is a pyramid
// at nine sides and a lampshade at forty; canvas over poles is a bell
// with folds. Around: 64 steps. Down: 12 rows. r(v) = Rt·v^0.72 (the
// concave sag), ridged by cos(9θ) so the folds read, the hem drooping
// between ridges so it scallops. The mouth is a missing arc.
function canopyGeometry(Rt, H, Ez, gapCenter, gapHalf) {
  const SEG = 72, ROWS = 12, RIDGES = 9, V_CUT = 0.5;
  const pos = [], uv = [], idx = [];
  const pt = (th, v, ridge) => {
    const r = Rt * Math.pow(v, 0.72) * (1 + 0.05 * ridge * v * v);
    let y = H * (1 - v) - H * 0.10 * Math.sin(v * Math.PI);
    if (v > 0.82) y -= (0.10 + 0.22 * (0.5 - 0.5 * ridge)) * (v - 0.82) / 0.18;   // the scalloped hem
    return [Math.sin(th) * r, y, Math.cos(th) * r * Ez];
  };
  for (let i = 0; i <= SEG; i++) {
    const th = Math.PI * 2 * i / SEG;
    const ridge = Math.cos(th * RIDGES);
    // the mouth: within the gap the skirt below V_CUT is LIFTED — every
    // point folds up to the cut line, so the canvas stays closed over
    // the top and opens only where the poles hold it up. k is the soft
    // edge, so the fold reads as a rolled hem and not a razor.
    let d = Math.abs(((th - gapCenter) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
    const k = Math.max(0, Math.min(1, (gapHalf - d) / 0.10));
    const cut = pt(th, V_CUT, ridge);
    for (let j = 0; j <= ROWS; j++) {
      const v = j / ROWS;
      const p = pt(th, v, ridge);
      if (v > V_CUT && k > 0) { const f = k * 0.92; p[0] += (cut[0] - p[0]) * f; p[1] += (cut[1] - 0.15 - p[1]) * f; p[2] += (cut[2] - p[2]) * f; }
      pos.push(p[0], p[1], p[2]);
      uv.push(i / SEG * 4, 1 - v);
    }
  }
  for (let i = 0; i < SEG; i++) for (let j = 0; j < ROWS; j++) {
    const a = i * (ROWS + 1) + j, b = a + ROWS + 1;
    idx.push(a, a + 1, b, b, a + 1, b + 1);   // wound so the OUTSIDE is the front face
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}
// `wallZ`: the south balustrade's inner face (ROWS - 0.75). Since 2026-09-22 the bell
// spans from the box's north edge to that wall, instead of (d + 2.6) deep round its
// centre — which ran the skirt past the balustrade and over the drop.
export function WorkshopTent({ rects, wallZ = 19.25 }) {
  const b = rectBounds(rects);
  const tex = useMemo(() => { const t = tentTex(); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t; }, []);
  const wood = useMemo(() => woodTex(), []);
  const fire = useRef(); const ember = useRef(); const light = useRef(); const gems = useRef([]);
  // the mouth: centred a hair west of south (θ=0 is +z = south; -x is 1.5π)
  const GAP_C = -Math.PI * 0.18, GAP_H = Math.PI * 0.21;
  const span = b ? wallZ - 0.15 - b.y0 : 0;
  const dims = useMemo(() => b ? { Rt: b.w * 0.5, Ez: (span / 2) / (b.w * 0.5), H: b.w * 0.3 } : null, [b, span]);
  const geo = useMemo(() => dims ? canopyGeometry(dims.Rt, dims.H, dims.Ez, GAP_C, GAP_H) : null, [dims]);   // eslint-disable-line react-hooks/exhaustive-deps
  const ropes = useMemo(() => {
    const R = mulberry32(0x7e7e); const out = [];
    for (let i = 0; i < 11; i++) { const a = GAP_C + GAP_H + 0.12 + (Math.PI * 2 - GAP_H * 2 - 0.24) * i / 10; out.push({ a, l: 0.9 + R() * 0.6, tilt: (R() - 0.5) * 0.5 }); }
    return out;
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const k = 1 + Math.sin(t * 5.3) * 0.08 + Math.sin(t * 9.1) * 0.05;
    if (fire.current) { fire.current.scale.set(1, k, 1); fire.current.material.opacity = 0.75 + Math.sin(t * 7) * 0.1; }
    if (ember.current) ember.current.material.emissiveIntensity = 0.8 + Math.sin(t * 2.2) * 0.25;
    if (light.current) light.current.intensity = G(LIGHT.forge) * k;
    for (let i = 0; i < gems.current.length; i++) { const g = gems.current[i]; if (g) g.material.emissiveIntensity = 0.6 + Math.sin(t * 1.9 + i) * 0.3; }
  });
  if (!b || !dims) return null;
  const { Rt, Ez, H } = dims;
  const cx = b.cx, cz = b.y0 + span / 2;
  const doorX = b.x0 + 0.6, doorZ = b.y1 + 1 - 1.0;
  const peg = (a, l, tilt, i) => {
    const px = Math.sin(a) * (Rt + l), pz = Math.cos(a) * (Rt * Ez + l * Ez);
    const hx = Math.sin(a) * Rt * 0.97, hz = Math.cos(a) * Rt * Ez * 0.97, hy = H * 0.12;
    const dx = px - hx, dz = pz - hz, dy = 0.1 - hy, len = Math.hypot(dx, dy, dz);
    return (
      <group key={i}>
        <group position={[(px + hx) / 2, (hy + 0.1) / 2, (pz + hz) / 2]} rotation={[0, Math.atan2(dx, dz), 0]}>
          <group rotation={[Math.PI / 2 - Math.atan2(-dy, Math.hypot(dx, dz)), 0, 0]}><mesh><cylinderGeometry args={[0.018, 0.018, len, 4]} /><meshStandardMaterial color="#8a7454" roughness={1} /></mesh></group>
        </group>
        <mesh position={[px, 0.16, pz]} rotation={[0.35 + tilt, 0, tilt]}><cylinderGeometry args={[0.05, 0.04, 0.36, 5]} /><meshStandardMaterial map={wood} /></mesh>
      </group>
    );
  };
  return (
    <group position={[cx, 0, cz]}>
      {/* the canvas, and its dark inside */}
      <mesh geometry={geo} castShadow receiveShadow><meshStandardMaterial map={tex} side={THREE.FrontSide} roughness={1} /></mesh>
      <mesh geometry={geo} scale={[0.995, 0.995, 0.995]}><meshStandardMaterial color="#241a18" side={THREE.BackSide} roughness={1} /></mesh>
      {/* the centre pole, through the peak, and its finial */}
      <mesh position={[0, H / 2, 0]}><cylinderGeometry args={[0.07, 0.1, H + 0.3, 6]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, H + 0.25, 0]}><sphereGeometry args={[0.09, 8, 6]} /><meshStandardMaterial map={wood} /></mesh>
      {/* the two poles that hold the mouth open, leaning out */}
      {[-1, 1].map((s) => { const a = GAP_C + s * GAP_H * 0.9; const x = Math.sin(a) * Rt * 0.62, z = Math.cos(a) * Rt * Ez * 0.62; return (
        <mesh key={s} position={[x, H * 0.3, z]} rotation={[0.22, 0, -s * 0.1]}><cylinderGeometry args={[0.05, 0.07, H * 0.62, 6]} /><meshStandardMaterial map={wood} /></mesh>); })}
      {/* ropes to pegs, all the way round */}
      {ropes.map((r, i) => peg(r.a, r.l, r.tilt, i))}
      {/* INSIDE */}
      <group position={[0.4, 0, 0.6]}>
        {/* the forge: a stone ring, the ember, the fire */}
        <mesh position={[0, 0.32, 0]} castShadow><cylinderGeometry args={[0.6, 0.72, 0.64, 8]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
        <mesh position={[0, 0.65, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.58, 0.07, 6, 8]} /><meshStandardMaterial {...STONE} /></mesh>
        <mesh ref={ember} position={[0, 0.66, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.5, 8]} /><meshStandardMaterial color="#6a3ae0" emissive="#7c4cff" emissiveIntensity={0.9} toneMapped={false} /></mesh>
        <mesh ref={fire} position={[0, 0.95, 0]}><coneGeometry args={[0.34, 0.7, 7]} /><meshBasicMaterial color="#9fe8ff" transparent opacity={0.8} depthWrite={false} toneMapped={false} /></mesh>
        <pointLight ref={light} position={[0, 1.2, 0]} intensity={G(LIGHT.forge)} distance={9} decay={1.4} color="#7ac8ff" />
        {/* the bench, with a hammer and a blade laid on it */}
        <group position={[0, 0, -1.5]}>
          <mesh position={[0, 0.62, 0]} castShadow><boxGeometry args={[2.2, 0.1, 0.8]} /><meshStandardMaterial map={wood} /></mesh>
          {[-0.95, 0.95].map((x) => <mesh key={x} position={[x, 0.3, 0]}><boxGeometry args={[0.12, 0.6, 0.7]} /><meshStandardMaterial map={wood} /></mesh>)}
          <mesh position={[-0.5, 0.7, 0.1]} rotation={[0, 0.4, 0]}><boxGeometry args={[0.28, 0.08, 0.12]} /><meshStandardMaterial {...IRON} /></mesh>
          <mesh position={[-0.3, 0.7, 0.1]} rotation={[0, 0.4, 0]}><cylinderGeometry args={[0.02, 0.02, 0.4, 5]} /><meshStandardMaterial map={wood} /></mesh>
          <mesh position={[0.5, 0.69, 0]} rotation={[0, -0.3, 0]}><boxGeometry args={[0.7, 0.02, 0.09]} /><meshStandardMaterial color="#9a9ab0" metalness={0.8} roughness={0.3} /></mesh>
        </group>
        {/* crates, one open and full of N'ullus crystal */}
        <mesh position={[-1.7, 0.3, -0.4]} rotation={[0, 0.3, 0]} castShadow><boxGeometry args={[0.6, 0.6, 0.6]} /><meshStandardMaterial map={wood} /></mesh>
        <mesh position={[-1.6, 0.85, -0.4]} rotation={[0, -0.2, 0]}><boxGeometry args={[0.45, 0.4, 0.45]} /><meshStandardMaterial map={wood} /></mesh>
        <group position={[-1.6, 1.05, -0.4]}>
          {[[0, 0, 0, 0.12], [0.1, 0, 0.08, 0.08], [-0.09, 0, 0.06, 0.07], [0.02, 0, -0.1, 0.09]].map((g, k) => <mesh key={k} ref={(el) => { gems.current[k] = el; }} position={[g[0], g[3] * 0.8, g[2]]} rotation={[0.3 * k, k, 0.2]}><octahedronGeometry args={[g[3], 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.7} roughness={0.15} toneMapped={false} /></mesh>)}
        </group>
        <group position={[-1.9, 0, 0.9]}>
          {[[0, 0, 0, 0.14], [0.12, 0, 0.1, 0.09], [-0.1, 0, 0.08, 0.08]].map((g, k) => <mesh key={k} ref={(el) => { gems.current[4 + k] = el; }} position={[g[0], g[3] * 0.8, g[2]]} rotation={[0.3 * k, k * 1.4, 0.2]}><octahedronGeometry args={[g[3], 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.7} roughness={0.15} toneMapped={false} /></mesh>)}
        </group>
        {/* the anvil on its stump */}
        <group position={[1.6, 0, 0.2]}>
          <mesh position={[0, 0.25, 0]}><cylinderGeometry args={[0.22, 0.26, 0.5, 7]} /><meshStandardMaterial map={wood} /></mesh>
          <mesh position={[0, 0.6, 0]}><boxGeometry args={[0.7, 0.2, 0.3]} /><meshStandardMaterial {...IRON} /></mesh>
          <mesh position={[0.42, 0.62, 0]} rotation={[0, 0, Math.PI / 2]}><coneGeometry args={[0.1, 0.3, 6]} /><meshStandardMaterial {...IRON} /></mesh>
        </group>
        {/* the rack of blades against the back */}
        <group position={[1.9, 0, -1.4]}>
          {[-0.4, 0.4].map((x) => <mesh key={x} position={[x, 0.7, 0]}><boxGeometry args={[0.08, 1.4, 0.08]} /><meshStandardMaterial map={wood} /></mesh>)}
          <mesh position={[0, 1.2, 0]}><boxGeometry args={[0.95, 0.06, 0.1]} /><meshStandardMaterial map={wood} /></mesh>
          {[-0.25, 0, 0.25].map((x, k) => <mesh key={k} position={[x, 0.75, 0.04]} rotation={[0, 0, (k - 1) * 0.08]}><boxGeometry args={[0.07, 0.9, 0.02]} /><meshStandardMaterial color="#9a9ab0" metalness={0.8} roughness={0.3} /></mesh>)}
        </group>
        {/* a blade dropped on the floor, a scattering of scrap */}
        <mesh position={[0.9, 0.02, 1.2]} rotation={[-Math.PI / 2, 0, 0.7]}><planeGeometry args={[0.9, 0.1]} /><meshStandardMaterial color="#8a8aa0" metalness={0.8} roughness={0.35} side={THREE.DoubleSide} /></mesh>
      </group>
      {/* the door mat of light where the mouth opens */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[doorX - cx, 0.02, doorZ - cz]}>
        <circleGeometry args={[0.9, 12]} />
        <meshBasicMaterial color="#7ac8ff" transparent opacity={0.06} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE UNSTABLE WORLD — past the walls. Standing doctrine (ie-unstable-
// world): the Plaza is V'ael's own Emperium, so the reading is HERS —
// it hangs over an ocean of ANIMA (the life every Aenarchist spends at
// her tables), under an eclipse rimmed in O'mnis and cracked by
// N'ullus, the same violet as her fountain. Zero lights, fog={false},
// one useFrame, everything measured against the dome.
// ═══════════════════════════════════════════════════════════════════
const eclipseTex = () => canvasTex("plaza3d:eclipse", 512, 512, (g, w, h) => {
  const R = mulberry32(0xec1);
  const cx = w / 2, cy = h / 2, r = 150;
  g.clearRect(0, 0, w, h);
  const cor = g.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 1.7);
  cor.addColorStop(0, C.omnis + "aa"); cor.addColorStop(0.5, C.omnis + "33"); cor.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = cor; g.fillRect(0, 0, w, h);
  g.fillStyle = "#140A22"; g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
  g.strokeStyle = C.omnis; g.lineWidth = 6; g.shadowColor = C.omnis; g.shadowBlur = 24; g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
  // the crack — branching once at the midpoint
  g.strokeStyle = C.nullus; g.shadowColor = C.nullus; g.shadowBlur = 18; g.lineWidth = 4; g.lineCap = "round";
  let x = cx - r * 0.7, y = cy + r * 0.5; g.beginPath(); g.moveTo(x, y);
  const pts = [];
  for (let i = 0; i < 9; i++) { x += r * 0.16 + (R() - 0.5) * 20; y -= r * 0.12 + (R() - 0.5) * 30; g.lineTo(x, y); pts.push([x, y]); }
  g.stroke();
  const [bx, by] = pts[4]; g.lineWidth = 2.5; g.beginPath(); g.moveTo(bx, by);
  x = bx; y = by; for (let i = 0; i < 5; i++) { x += 10 + R() * 14; y += 14 + (R() - 0.5) * 12; g.lineTo(x, y); } g.stroke();
});
const skyTex = () => canvasTex("plaza3d:sky", 512, 1024, (g, w, h) => {
  const R = mulberry32(0x5c7);
  const v = g.createLinearGradient(0, 0, 0, h);
  v.addColorStop(0, "#07040f"); v.addColorStop(0.5, "#160c2a"); v.addColorStop(0.72, "#2a1540"); v.addColorStop(1, "#0a0512");
  g.fillStyle = v; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 7; i++) {
    const y = h * (0.3 + R() * 0.5), bh = 30 + R() * 90;
    const b = g.createLinearGradient(0, y - bh, 0, y + bh);
    const col = R() < 0.5 ? "179,136,255" : "242,193,78";
    b.addColorStop(0, `rgba(${col},0)`); b.addColorStop(0.5, `rgba(${col},${0.05 + R() * 0.09})`); b.addColorStop(1, `rgba(${col},0)`);
    g.fillStyle = b; g.fillRect(0, y - bh, w, bh * 2);
  }
  g.strokeStyle = "rgba(179,136,255,0.16)"; g.lineWidth = 1;
  for (let i = 0; i < 9; i++) { let x = R() * w, y = R() * h; g.beginPath(); g.moveTo(x, y); for (let k = 0; k < 8; k++) { x += (R() - 0.5) * 70; y += (R() - 0.5) * 70; g.lineTo(x, y); } g.stroke(); }
  for (let i = 0; i < N_STARS; i++) { g.fillStyle = `rgba(255,255,255,${0.25 + R() * 0.7})`; const s = R() < 0.9 ? 1 : 2; g.fillRect(R() * w, R() * h * 0.7, s, s); }
});
const oceanTex = () => canvasTex("plaza3d:ocean", 512, 512, (g, w, h) => {
  const R = mulberry32(0x0cea);
  g.fillStyle = "#3a0a24"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 140; i++) {
    g.strokeStyle = `rgba(255,${77 + R() * 60 | 0},${141 + R() * 60 | 0},${0.08 + R() * 0.25})`; g.lineWidth = 1 + R() * 2;
    let x = R() * w, y = R() * h; g.beginPath(); g.moveTo(x, y);
    for (let k = 0; k < 6; k++) { x += (R() - 0.5) * 60; y += (R() - 0.5) * 60; g.lineTo(x, y); }
    g.stroke();
  }
});
export function UnstableWorld({ cx, cz, R }) {
  const dome = Math.max(300, 3.4 * R);
  const sky = useMemo(() => skyTex(), []);
  const ecl = useMemo(() => eclipseTex(), []);
  const ocean = useMemo(() => { const t = oceanTex(); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(9, 9); return t; }, []);
  const shards = useMemo(() => {
    const Rn = mulberry32(0x5a7d);
    return Array.from({ length: N_SHARDS }, (_, i) => {
      const a = Rn() * Math.PI * 2, d = R * (2.3 + Rn() * 1.1);
      return { x: cx + Math.cos(a) * d, z: cz + Math.sin(a) * d, y: R * 0.1 + Rn() * R * 1.1, s: R * (0.04 + Rn() * 0.11), sx: 0.6 + Rn(), sy: 0.4 + Rn() * 0.7, r: Rn() * 6, spin: (Rn() - 0.5) * 0.12, bob: Rn() * 6, c: Rn() < 0.5 ? C.nullus : C.omnis };
    });
  }, [cx, cz, R]);
  const motes = useMemo(() => {
    const Rn = mulberry32(0x0713);
    return Array.from({ length: N_MOTES }, () => ({ x: cx + (Rn() - 0.5) * R * 2.6, z: cz + (Rn() - 0.5) * R * 2.6, y: Rn() * R * 0.8, v: 0.2 + Rn() * 0.5, ph: Rn() * 6, c: Rn() < 0.5 ? C.lyt : C.lyf }));
  }, [cx, cz, R]);
  const shardRefs = useRef([]); const moteRefs = useRef([]); const eclRef = useRef(); const eclMat = useRef(); const oceanRef = useRef();
  useFrame(({ clock, camera }) => {
    const t = clock.getElapsedTime();
    for (let i = 0; i < shards.length; i++) { const o = shardRefs.current[i]; if (!o) continue; o.rotation.y += shards[i].spin * 0.016; o.position.y = shards[i].y + Math.sin(t * 0.3 + shards[i].bob) * R * 0.02; }
    for (let i = 0; i < motes.length; i++) {
      const o = moteRefs.current[i]; if (!o) continue; const m = motes[i];
      const y = (m.y + t * m.v) % (R * 0.9); o.position.y = y;
      o.material.opacity = 0.35 + Math.sin(t * 1.1 + m.ph) * 0.3;
    }
    if (eclRef.current) { eclRef.current.lookAt(camera.position); }
    if (eclMat.current) eclMat.current.opacity = 0.92 + Math.sin(t * 0.7) * 0.06;
    if (oceanRef.current) { ocean.offset.set(t * 0.004, t * 0.0025); }
  });
  const ex = cx - R * 2.6, ez = cz - R * 1.6, ey = R * 1.3;   // ~3.3R out, well under the dome (3.4R+)
  return (
    <group>
      <mesh position={[cx, 0, cz]} renderOrder={-1}>
        <sphereGeometry args={[dome, 24, 16]} />
        <meshBasicMaterial map={sky} side={THREE.BackSide} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      {/* the ocean of Anima, far below */}
      <mesh ref={oceanRef} position={[cx, -R * 0.42, cz]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[R * 7, R * 7]} />
        <meshBasicMaterial map={ocean} fog={false} toneMapped={false} />
      </mesh>
      {/* the cracked eclipse — V'ael's Sol, in her own sky */}
      <mesh ref={eclRef} position={[ex, ey, ez]}>
        <planeGeometry args={[R * 1.5, R * 1.5]} />
        <meshBasicMaterial ref={eclMat} map={ecl} transparent depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      {/* shattered pocket dimensions, lit from inside */}
      {shards.map((s, i) => (
        <group key={i} ref={(el) => { shardRefs.current[i] = el; }} position={[s.x, s.y, s.z]} rotation={[s.r, s.r * 0.7, 0]} scale={[s.s * s.sx, s.s * s.sy, s.s]}>
          <mesh><dodecahedronGeometry args={[1, 0]} /><meshStandardMaterial color="#1a1030" roughness={0.9} flatShading fog={false} /></mesh>
          <mesh scale={0.74}><dodecahedronGeometry args={[1, 0]} /><meshBasicMaterial color={s.c} toneMapped={false} fog={false} /></mesh>
          <mesh scale={1.5}><dodecahedronGeometry args={[1, 0]} /><meshBasicMaterial color={s.c} transparent opacity={0.08} side={THREE.BackSide} depthWrite={false} fog={false} toneMapped={false} /></mesh>
        </group>
      ))}
      {/* the rise */}
      {motes.map((m, i) => (
        <mesh key={i} ref={(el) => { moteRefs.current[i] = el; }} position={[m.x, m.y, m.z]}>
          <sphereGeometry args={[R * 0.012, 5, 5]} />
          <meshBasicMaterial color={m.c} transparent opacity={0.5} depthWrite={false} fog={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
